import type { SearchClient } from "algoliasearch";

import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import {
  FACET_FETCH_LIMIT,
  QUERY_URL_PARAM,
} from "@/components/Search/constants";
import {
  DEFAULT_QUERY_SUGGESTIONS_INDEX,
  DEFAULT_TYPING_STATE_STRATEGY,
  type TypingStateStrategy,
} from "@/monarch/rules/productSearch/types";

export interface QueryHint {
  name: string;
  count: number;
  categoryUrl: string;
}

export const QUERY_HINT_LIMIT = 7;

export const buildSearchQueryUrl = (query: string) =>
  `/search?${QUERY_URL_PARAM}=${encodeURIComponent(query)}`;

export const buildAttributesFacetUrl = (attribute: string) =>
  `/search?attributes=${encodeURIComponent(attribute)}`;

const normalizeHintKey = (value: string) => value.trim().toLowerCase();

export const mergeFacetCounts = (
  facetGroups: Record<string, number>[],
): Map<string, { name: string; count: number }> => {
  const merged = new Map<string, { name: string; count: number }>();

  facetGroups.forEach((facets) => {
    Object.entries(facets).forEach(([name, count]) => {
      const key = normalizeHintKey(name);
      const existing = merged.get(key);
      if (!existing || count > existing.count) {
        merged.set(key, { name, count });
      }
    });
  });

  return merged;
};

export const buildQueryHintsFromFacets = (
  facetGroups: Record<string, number>[],
  query: string,
  {
    limit = QUERY_HINT_LIMIT,
    buildUrl = buildSearchQueryUrl,
    appendRawQuery = true,
  }: {
    limit?: number;
    buildUrl?: (value: string) => string;
    appendRawQuery?: boolean;
  } = {},
): QueryHint[] => {
  const queryLower = query.trim().toLowerCase();
  if (!queryLower) return [];

  const merged = mergeFacetCounts(facetGroups);
  const results = Array.from(merged.entries())
    .filter(([key]) => key.includes(queryLower))
    .map(([, value]) => ({
      name: value.name,
      count: value.count,
      categoryUrl: buildUrl(value.name),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  if (!appendRawQuery) {
    return results;
  }

  const hasExactQuery = results.some(
    (hint) => normalizeHintKey(hint.name) === queryLower,
  );

  if (!hasExactQuery && query.trim().length > 0 && results.length < limit) {
    results.push({
      name: query.trim(),
      count: 0,
      categoryUrl: buildUrl(query.trim()),
    });
  }

  return results.slice(0, limit);
};

type QuerySuggestionHit = {
  query?: string;
  popularity?: number;
};

export const mapQuerySuggestionHits = (
  hits: QuerySuggestionHit[],
  limit = QUERY_HINT_LIMIT,
): QueryHint[] =>
  hits
    .flatMap((hit) => {
      const name = hit.query?.trim();
      if (!name) return [];

      return [
        {
          name,
          count: hit.popularity ?? 0,
          categoryUrl: buildSearchQueryUrl(name),
        },
      ];
    })
    .slice(0, limit);

export const getQuerySuggestions = async (
  client: SearchClient,
  query: string,
  suggestionsIndex = DEFAULT_QUERY_SUGGESTIONS_INDEX,
): Promise<QueryHint[]> => {
  const trimmed = query.trim();
  if (!trimmed || !suggestionsIndex) return [];

  const { hits } = await client.searchSingleIndex({
    indexName: suggestionsIndex,
    searchParams: {
      query: trimmed,
      hitsPerPage: QUERY_HINT_LIMIT,
    },
  });

  return mapQuerySuggestionHits(hits as QuerySuggestionHit[]);
};

export const getAttributesQueryHints = async (
  client: SearchClient,
  query: string,
  indexName: string = PRODUCT_INDEX_EN,
): Promise<QueryHint[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const attributesFacetName = getFacetAttributeName("attributes", {
    indexName,
  });

  const response = await client.searchSingleIndex({
    indexName,
    searchParams: {
      query: "",
      hitsPerPage: 0,
      facets: [attributesFacetName],
      maxValuesPerFacet: FACET_FETCH_LIMIT,
    },
  });

  return buildQueryHintsFromFacets(
    [response.facets?.[attributesFacetName] ?? {}],
    trimmed,
    {
      buildUrl: buildAttributesFacetUrl,
      appendRawQuery: false,
    },
  );
};

export const getSkillsQueryHints = async (
  client: SearchClient,
  query: string,
  indexName: string = PRODUCT_INDEX_EN,
): Promise<QueryHint[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const skillsFacet = getFacetAttributeName("skills.skill", { indexName });

  const response = await client.searchSingleIndex({
    indexName,
    searchParams: {
      query: trimmed,
      hitsPerPage: 0,
      facets: [skillsFacet],
      maxValuesPerFacet: 100,
    },
  });

  return buildQueryHintsFromFacets(
    [response.facets?.[skillsFacet] ?? {}],
    trimmed,
  );
};

export const getSearchQueryHints = async (
  client: SearchClient,
  query: string,
  indexName: string = PRODUCT_INDEX_EN,
  strategy: TypingStateStrategy = DEFAULT_TYPING_STATE_STRATEGY,
  querySuggestionsIndex: string = DEFAULT_QUERY_SUGGESTIONS_INDEX,
): Promise<QueryHint[]> => {
  switch (strategy) {
    case "skills":
      return getSkillsQueryHints(client, query, indexName);
    case "querySuggestions": {
      try {
        const suggestions = await getQuerySuggestions(
          client,
          query,
          querySuggestionsIndex,
        );
        if (suggestions.length > 0) {
          return suggestions;
        }
      } catch {
        // Fall through when the suggestions index is missing or unavailable.
      }
      return getSkillsQueryHints(client, query, indexName);
    }
    case "attributes":
    default:
      return getAttributesQueryHints(client, query, indexName);
  }
};
